/**
 * Provider 适配器共用的 HTTP 基础设施（T03）。
 *
 * 这里集中三件事，避免每个适配器各写一套：
 * 1. baseUrl 规范化（补 `/v1`）；
 * 2. 超时 + 外部 AbortSignal 的组合取消；
 * 3. 失败重试 1 次的策略；
 * 4. SSE（`text/event-stream`）跨 chunk 半行拼接解析。
 *
 * 全部使用原生 `fetch`，不引入任何 SDK。
 */

import { wrapProviderFetch } from './providerFetch';
import type { FetchLike, ProviderDeps, ProviderErrorCode } from './types';

export const DEFAULT_TIMEOUT_MS = 60_000;
export const DEFAULT_RETRIES = 1;
export const DEFAULT_RETRY_DELAY_MS = 300;

/** 规范化后的错误，供适配器统一转成 `StreamChunk{type:'error'}`。 */
export class ProviderHttpError extends Error {
  readonly code: ProviderErrorCode;
  readonly status?: number;

  constructor(message: string, code: ProviderErrorCode, status?: number) {
    super(message);
    this.name = 'ProviderHttpError';
    this.code = code;
    this.status = status;
  }
}

/** 版本段：`/v1`、`/v2`、`/v1beta`、`/v1-preview` 等。 */
const VERSION_SUFFIX = /\/v\d+[a-z0-9-]*$/i;

/**
 * 统一 baseUrl 规范化：去掉尾部斜杠，并在 pathname 缺少版本段时补上 `/v1`。
 *
 * query 与 hash 原样保留，且不参与版本段判断（`new URL()` 解析 pathname）。
 *
 * - `https://api.openai.com`                  -> `https://api.openai.com/v1`
 * - `https://api.openai.com/`                 -> `https://api.openai.com/v1`
 * - `https://api.openai.com/v1`               -> 原样
 * - `https://api.openai.com/v1/`              -> `https://api.openai.com/v1`
 * - `https://openrouter.ai/api/v1`            -> 原样
 * - `https://generativelanguage.googleapis.com/v1beta` -> 原样
 * - `https://my-gateway.example.com/openai`   -> `https://my-gateway.example.com/openai/v1`
 * - `https://x.test/api?k=1`                  -> `https://x.test/api/v1?k=1`
 * - `https://x.test/v1?k=1`                   -> 原样
 */
export function normalizeBaseUrl(rawBaseUrl: string): string {
  const trimmed = (rawBaseUrl ?? '').trim();
  if (!trimmed) {
    throw new ProviderHttpError('baseUrl 为空', 'config');
  }

  let parsed: URL;
  try {
    parsed = new URL(trimmed);
  } catch {
    throw new ProviderHttpError(`baseUrl 非法: ${rawBaseUrl}`, 'config');
  }

  // 仅对 pathname 去尾斜杠并判断版本段；search / hash 不动。
  const pathname = parsed.pathname.replace(/\/+$/, '');
  parsed.pathname = VERSION_SUFFIX.test(pathname) ? pathname : `${pathname}/v1`;

  // URL 序列化会保留 search / hash；pathname 非空，因此不会产生多余尾斜杠。
  return parsed.toString();
}

/**
 * 在规范化 baseUrl 之后追加路径段。
 *
 * 必须走 `URL` 拼接而不是字符串相加：baseUrl 可能带 query / hash，
 * 直接拼会得到 `.../v1?k=1/models` 这种非法地址。
 */
function appendPath(baseUrl: string, segment: string): string {
  const url = new URL(normalizeBaseUrl(baseUrl));
  url.pathname = `${url.pathname.replace(/\/+$/, '')}${segment}`;
  return url.toString();
}

/** `/v1/chat/completions` 完整地址。 */
export function chatCompletionsUrl(baseUrl: string): string {
  return appendPath(baseUrl, '/chat/completions');
}

/** `/v1/models` 完整地址。 */
export function modelsUrl(baseUrl: string): string {
  return appendPath(baseUrl, '/models');
}

/** 判断错误是否为取消（外部取消或超时）。 */
export function isAbortError(error: unknown): boolean {
  return (
    !!error &&
    typeof error === 'object' &&
    'name' in error &&
    (error as { name?: unknown }).name === 'AbortError'
  );
}

/** 把任意抛出物规范化成 `ProviderHttpError`。 */
export function toProviderError(
  error: unknown,
  fallbackCode: ProviderErrorCode = 'network',
): ProviderHttpError {
  if (error instanceof ProviderHttpError) return error;
  if (error instanceof DOMException && error.name === 'AbortError') {
    return new ProviderHttpError('请求已被取消', 'aborted');
  }
  if (isAbortError(error)) {
    return new ProviderHttpError('请求已被取消', 'aborted');
  }
  if (error instanceof Error) {
    return new ProviderHttpError(error.message, fallbackCode);
  }
  return new ProviderHttpError(String(error), fallbackCode);
}

/** 可重试的判定：网络错误 / 超时 / 5xx / 429。取消与 4xx 不重试。 */
export function isRetryable(error: ProviderHttpError): boolean {
  if (error.code === 'network' || error.code === 'timeout') return true;
  if (error.code === 'http' && error.status !== undefined) {
    return error.status >= 500 || error.status === 429;
  }
  return false;
}

/** 重试次数钳制：非法 / 负数 -> 0，小数向下取整。 */
export function clampRetries(value: number | undefined, fallback: number): number {
  if (value === undefined || !Number.isFinite(value)) return fallback;
  return Math.max(0, Math.floor(value));
}

/** 超时钳制：非法 / 负数 -> 0（0 表示不超时）。 */
export function clampTimeoutMs(value: number | undefined, fallback: number): number {
  if (value === undefined || !Number.isFinite(value)) return fallback;
  return Math.max(0, value);
}

/** 重试间隔钳制：非法 / 负数 -> 0。 */
export function clampRetryDelayMs(value: number | undefined, fallback: number): number {
  if (value === undefined || !Number.isFinite(value)) return fallback;
  return Math.max(0, value);
}

export interface ResolvedDeps {
  fetch: FetchLike;
  timeoutMs: number;
  retries: number;
  retryDelayMs: number;
  extraHeaders: Record<string, string>;
}

export function resolveDeps(deps?: ProviderDeps): ResolvedDeps {
  const globalFetch = (globalThis as { fetch?: FetchLike }).fetch;
  const fetchImpl = deps?.fetch ?? globalFetch;
  if (typeof fetchImpl !== 'function') {
    throw new ProviderHttpError('当前运行环境没有可用的 fetch', 'config');
  }
  // 浏览器里 `window.fetch` 必须带着 Window 这个接收者调用，否则会抛
  // `TypeError: Failed to execute 'fetch' on 'Window': Illegal invocation`。
  // 从 globalThis 上「摘下来」再单独调用就会丢 this，因此这里显式绑定；
  // 注入的测试替身不动（它们是普通函数，绑定反而会改变其语义）。
  // 走到这里 fetchImpl 已确认是函数；默认分支要用宿主 fetch 时必须绑定
  // 接收者（否则浏览器抛 Illegal invocation），注入分支保持原样。
  // 默认宿主 fetch 使用开发代理或云端认证转发，注入的 transport 保持原样。
  const boundFetch: FetchLike =
    deps?.fetch ?? wrapProviderFetch(globalFetch!.bind(globalThis));
  return {
    fetch: boundFetch,
    timeoutMs: clampTimeoutMs(deps?.timeoutMs, DEFAULT_TIMEOUT_MS),
    retries: clampRetries(deps?.retries, DEFAULT_RETRIES),
    retryDelayMs: clampRetryDelayMs(deps?.retryDelayMs, DEFAULT_RETRY_DELAY_MS),
    extraHeaders: { ...(deps?.extraHeaders ?? {}) },
  };
}

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

async function safeReadText(response: Response): Promise<string> {
  try {
    const text = await response.text();
    return text.slice(0, 500);
  } catch {
    return '';
  }
}

/**
 * 解析 SSE 流，逐条产出 `data:` 负载字符串。
 *
 * 处理要点：
 * - 跨 chunk 的半行拼接（`TextDecoder({stream:true})` + 行缓冲）；
 * - `\n` 与 `\r\n` 两种行尾；
 * - 忽略 `:` 开头的注释行与 `event:` / `id:` 等非 data 字段；
 * - 空行作为事件分隔符；
 * - 流自然结束时 flush 尾部残留数据；
 * - **消费方提前 break / return（例如收到 `[DONE]`）时，在 `finally` 中
 *   `reader.cancel()` 主动释放底层 body**，避免服务端 keep-alive 造成的
 *   悬挂连接与连接池泄漏。
 */
export async function* readSSEData(
  stream: ReadableStream<Uint8Array>,
  signal?: AbortSignal,
): AsyncGenerator<string, void, undefined> {
  const reader = stream.getReader();
  const decoder = new TextDecoder('utf-8');
  let buffer = '';
  let dataLines: string[] = [];
  let drained = false;

  const flushEvent = (): string | undefined => {
    if (dataLines.length === 0) return undefined;
    const payload = dataLines.join('\n');
    dataLines = [];
    return payload;
  };

  const consumeLine = (rawLine: string): string | undefined => {
    const line = rawLine.endsWith('\r') ? rawLine.slice(0, -1) : rawLine;
    if (line === '') return flushEvent();
    if (line.startsWith(':')) return undefined;
    if (line.startsWith('data:')) {
      const value = line.slice(5);
      dataLines.push(value.startsWith(' ') ? value.slice(1) : value);
      return undefined;
    }
    // event: / id: / retry: 等字段当前不使用，忽略。
    return undefined;
  };

  const onAbort = () => {
    void reader.cancel().catch(() => undefined);
  };
  signal?.addEventListener('abort', onAbort, { once: true });

  try {
    while (true) {
      if (signal?.aborted) return;
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      let newlineIndex = buffer.indexOf('\n');
      while (newlineIndex !== -1) {
        const rawLine = buffer.slice(0, newlineIndex);
        buffer = buffer.slice(newlineIndex + 1);
        const payload = consumeLine(rawLine);
        if (payload !== undefined) yield payload;
        newlineIndex = buffer.indexOf('\n');
      }
    }

    // 被取消时不产出尾部残留数据。
    if (signal?.aborted) return;

    buffer += decoder.decode();
    if (buffer.length > 0) {
      const payload = consumeLine(buffer);
      if (payload !== undefined) yield payload;
      buffer = '';
    }
    const tail = flushEvent();
    if (tail !== undefined) yield tail;

    drained = true;
  } finally {
    signal?.removeEventListener('abort', onAbort);
    if (!drained) {
      // 消费方提前退出（[DONE] / 内联 error / 取消 / 抛异常）：
      // 主动取消底层 body，释放连接。
      try {
        await reader.cancel();
      } catch {
        // 流已 errored 或已释放，忽略。
      }
    }
    reader.releaseLock();
  }
}

/** 读取响应体为文本（非流式分支）。 */
export async function readJsonBody<T>(response: Response): Promise<T> {
  const text = await response.text();
  if (!text) {
    throw new ProviderHttpError('响应体为空', 'parse', response.status);
  }
  try {
    return JSON.parse(text) as T;
  } catch {
    throw new ProviderHttpError(`响应不是合法 JSON: ${text.slice(0, 200)}`, 'parse', response.status);
  }
}

/** 组装 JSON 请求的通用头。 */
export function jsonHeaders(apiKey?: string, extra?: Record<string, string>): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    ...extra,
  };
  if (apiKey) headers.Authorization = `Bearer ${apiKey}`;
  return headers;
}

/** 流式请求需要 `Accept: text/event-stream`。 */
export function streamHeaders(apiKey?: string, extra?: Record<string, string>): Record<string, string> {
  return { ...jsonHeaders(apiKey, extra), Accept: 'text/event-stream' };
}

/* ------------------------------------------------------------------ *
 * 可重置超时（流式场景：按「空闲」计时，而不是总时长）
 * ------------------------------------------------------------------ */

/**
 * 超时守卫。流式响应每个 chunk 到达时 `reset()` 重新计时，
 * 于是「N 秒无数据」才判定超时，而不会因为一次长回答超过总时长被误杀。
 */
export class TimeoutGuard {
  private readonly controller = new AbortController();
  private timer: ReturnType<typeof setTimeout> | undefined;
  private didTimeOut = false;
  private disposed = false;
  private readonly onExternalAbort: () => void;

  constructor(
    private readonly timeoutMs: number,
    private readonly external?: AbortSignal,
  ) {
    this.onExternalAbort = () => this.controller.abort(this.external?.reason);
    if (external) {
      if (external.aborted) this.controller.abort(external.reason);
      else external.addEventListener('abort', this.onExternalAbort, { once: true });
    }
    this.reset();
  }

  get signal(): AbortSignal {
    return this.controller.signal;
  }

  timedOut(): boolean {
    return this.didTimeOut;
  }

  /** 重新开始计时；已销毁或已取消时不生效。 */
  reset(): void {
    if (this.disposed || this.controller.signal.aborted) return;
    if (this.timer !== undefined) clearTimeout(this.timer);
    if (this.timeoutMs <= 0) return;
    this.timer = setTimeout(() => {
      this.didTimeOut = true;
      this.controller.abort(new ProviderHttpError(`请求超时（${this.timeoutMs}ms）`, 'timeout'));
    }, this.timeoutMs);
  }

  /**
   * 主动中止底层请求。用于「消费方提前结束读取」时释放连接，
   * 避免服务端 keep-alive 留下悬挂的 response body。
   */
  abort(reason?: unknown): void {
    if (!this.controller.signal.aborted) {
      this.controller.abort(reason);
    }
  }

  dispose(): void {
    this.disposed = true;
    if (this.timer !== undefined) {
      clearTimeout(this.timer);
      this.timer = undefined;
    }
    this.external?.removeEventListener('abort', this.onExternalAbort);
  }
}

export interface OpenResponseResult {
  response: Response;
  guard: TimeoutGuard;
}

/**
 * 建立连接并按策略重试（只在「拿到响应头之前」重试）。
 *
 * 拿到 2xx 响应头后立即返回，超时守卫交给调用方，流式分支可在每个
 * chunk 上 `guard.reset()`；调用方负责 `guard.dispose()`（以及提前结束时
 * `guard.abort()`）。
 */
export async function openResponseWithRetry(options: {
  url: string;
  init: RequestInit;
  deps: ResolvedDeps;
  externalSignal?: AbortSignal;
}): Promise<OpenResponseResult> {
  const { url, init, deps, externalSignal } = options;
  let lastError: ProviderHttpError | undefined;

  for (let attempt = 0; attempt <= deps.retries; attempt += 1) {
    if (externalSignal?.aborted) {
      throw new ProviderHttpError('请求已被取消', 'aborted');
    }

    const guard = new TimeoutGuard(deps.timeoutMs, externalSignal);
    try {
      const response = await deps.fetch(url, { ...init, signal: guard.signal });
      if (!response.ok) {
        const body = await safeReadText(response);
        throw new ProviderHttpError(
          `HTTP ${response.status} ${response.statusText}${body ? `: ${body}` : ''}`,
          'http',
          response.status,
        );
      }
      return { response, guard };
    } catch (error) {
      guard.dispose();
      const normalized = toProviderError(error);
      if (externalSignal?.aborted) {
        throw new ProviderHttpError('请求已被取消', 'aborted');
      }
      const finalError = guard.timedOut()
        ? new ProviderHttpError(`请求超时（${deps.timeoutMs}ms）`, 'timeout')
        : normalized;

      lastError = finalError;
      if (attempt >= deps.retries || !isRetryable(finalError)) {
        throw finalError;
      }
      await sleep(deps.retryDelayMs);
    }
  }

  throw lastError ?? new ProviderHttpError('未知请求失败', 'unknown');
}
